import Backimg from "../../../assets/img/backicon01.svg";
import UserImg2 from "../../../assets/img/user-account2.svg";
import Arrow from "../../../assets/img/arrow-left-02.svg";
import lockIcon from "../../../assets/img/lockIcon.svg";
import Cancelimg from "../../../assets/img/cancel-01.svg";
import useTranslator from "../../../hooks/useTranslator";
import SkorDefaultLoader from "../../../assets/img/skorDefaultLoader.gif";
import KycVerificationPopup from "../KycVerificationPopup";
import { KycDocLoader } from "../../../components/Onboarding/KycDocLoader";
import ProcessFailedModal from "../ProcessFailedModal";
import { useNavigate } from "react-router-dom";
import { useKycWorkflow, KYC_STEP } from "./hooks/useKycWorkflow";
import { getDocumentsForCountry, COUNTRIES } from "./documentSelection";

import DocumentSelectScreen from "./components/DocumentSelectScreen";
import FileUploadScreen from "./components/FileUploadScreen";
import LoadingScreen from "./components/LoadingScreen";
import VerificationStartScreen from "./components/VerificationStartScreen";
import "./authenticateYourId.css";

interface AuthenticateYourIdProps {
  user: { _id: string; [key: string]: any };
  formHandler: { prev: () => void };
  updateUserProfile: (data: any) => void;
  isGuarantor: boolean;
  usedFor: string;
  setShowKycPopup: (show: boolean) => void;
  addedFiles: any[];
  setAddedFiles: (files: any[]) => void;
  getNextStep: () => number;
}

const AuthenticateYourId = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  usedFor,
  setShowKycPopup,
  addedFiles,
  setAddedFiles,
  getNextStep,
}: AuthenticateYourIdProps) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();

  const {
    failedKyc,
    setFailedKyc,
    customKycStarted,
    onfidoInitiateLoading,
    kycInitiationFailed,
    kycVerificationPopup,
    setKycVerificationPopup,
    showOnfidoWatingLoading,
    showKycLoading,
    showDocumentRetryMessage,
    kycStep,
    docType,
    issuingCountry,
    frontImage,
    setFrontImage,
    backImage,
    setBackImage,
    needsBackSide,
    startCustomKyc,
    handleStartVerification,
    handleCustomKycCancel,
    handleDocTypeChange,
    handleCountryChange,
    handleDocTypeSelect,
    handleFileUploadFront,
    handleFileUploadBack,
    handleFrontCapture,
    handleBackCapture,
    loadUser,
    setKycStep,
  } = useKycWorkflow({
    user,
    isGuarantor,
    usedFor,
    setShowKycPopup,
    setAddedFiles,
    getNextStep,
    updateUserProfile,
  });

  const getDocumentSubmitTitle = (side: string) => {
    const documentName =
      docType === "passport"
        ? _t("Passport")
        : docType === "residence_permit"
          ? _t("Residence permit")
          : _t("identity card");

    return _t(`Submit ${documentName} (${side})`);
  };

  const renderCaptureStep = ({
    title,
    subtitle,
    onFileUpload,
  }: {
    title: string;
    subtitle: string;
    onFileUpload: (file: File | Blob) => Promise<void>;
  }) => {
    return (
      <FileUploadScreen
        key={kycStep}
        title={title}
        subtitle={subtitle}
        onFileSelect={onFileUpload}
      />
    );
  };

  const getCustomKycBackHandler = () => {
    if (kycStep === KYC_STEP.CAPTURE_FRONT) {
      return () => setKycStep(KYC_STEP.SELECT_DOC);
    }

    if (kycStep === KYC_STEP.CAPTURE_BACK) {
      return () => setKycStep(KYC_STEP.CAPTURE_FRONT);
    }

    return null;
  };

  const renderCustomKycStep = () => {
    const availableDocs = getDocumentsForCountry(issuingCountry);

    switch (kycStep) {
      case KYC_STEP.START:
        return <VerificationStartScreen onStart={handleStartVerification} />;

      case KYC_STEP.SELECT_DOC:
        return (
          <DocumentSelectScreen
            issuingCountry={issuingCountry}
            docType={docType}
            availableCountries={COUNTRIES}
            availableDocs={availableDocs}
            onCountryChange={handleCountryChange}
            onDocTypeChange={handleDocTypeChange}
            onContinue={handleDocTypeSelect}
            showRetryMessage={showDocumentRetryMessage}
            _t={_t}
          />
        );

      case KYC_STEP.CAPTURE_FRONT:
        return renderCaptureStep({
          title:
            docType === "passport"
              ? _t("Submit passport")
              : getDocumentSubmitTitle(_t("front")),
          subtitle: "",
          onFileUpload: handleFileUploadFront,
        });

      case KYC_STEP.CAPTURE_BACK:
        return renderCaptureStep({
          title: getDocumentSubmitTitle(_t("back")),
          subtitle: _t(
            "Please upload a recent photo of your original document"
          ),
          onFileUpload: handleFileUploadBack,
        });

      case KYC_STEP.UPLOADING:
        return (
          <LoadingScreen
            message={_t("uploadingDocuments", "Uploading your documents...")}
          />
        );

      case KYC_STEP.PROCESSING:
        return (
          <LoadingScreen
            message={
              showOnfidoWatingLoading
                ? _t("pleaseWaitWhileWeAreFetchingYourKycDetails")
                : _t(
                    "processingYourVerification",
                    "Processing your verification..."
                  )
            }
          />
        );

      default:
        return null;
    }
  };

  const renderKycInitiationFailed = () => (
    <div className="custom-kyc-init-failed" role="alert">
      <div className="custom-kyc-init-failed-icon">!</div>
      <h3>
        {_t(
          "kycInitializationFailed",
          "We couldn't initialize your verification"
        )}
      </h3>
      <p>
        {_t(
          "kycInitializationFailedDescription",
          "Please check your connection and try initializing the verification again."
        )}
      </p>
      <button
        type="button"
        className="kyc-verification-start-button"
        disabled={onfidoInitiateLoading}
        onClick={startCustomKyc}
      >
        {_t("retryInitialization", "Retry initialization")}
      </button>
    </div>
  );

  const customKycBackHandler = getCustomKycBackHandler();

  return (
    <>
      {customKycStarted && (
        <div className="onfido-overlay">
          <div className="onfido-wrapper">
            <div className="custom-kyc-modal-toolbar">
              {customKycBackHandler ? (
                <button
                  type="button"
                  className="custom-kyc-toolbar-button"
                  onClick={customKycBackHandler}
                  aria-label={_t("Back")}
                >
                  <img src={Backimg} alt="" />
                </button>
              ) : (
                <span className="custom-kyc-toolbar-spacer" />
              )}
              <button
                type="button"
                className="custom-kyc-toolbar-button custom-kyc-toolbar-close"
                onClick={handleCustomKycCancel}
                aria-label={_t("Close")}
              >
                <img src={Cancelimg} alt="" />
              </button>
            </div>
            <div className="custom-kyc-container">
              {onfidoInitiateLoading ? (
                <LoadingScreen
                  message={_t(
                    "initializingKyc",
                    "Initializing KYC verification..."
                  )}
                />
              ) : kycInitiationFailed ? (
                renderKycInitiationFailed()
              ) : (
                <>{renderCustomKycStep()}</>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className={`approvedModal popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen ${
          isGuarantor ? "guarantorBg" : ""
        }`}
        style={{ zIndex: 99 }}
      >
        {!customKycStarted && (
          <div
            className="popup-content2 position-relative popup-content bg-white required-doc-popup onfiddoModal authenticate-your-id-modal"
            onClick={isGuarantor ? (e) => e.stopPropagation() : undefined}
          >
            {(isGuarantor || usedFor === "popup") && (
              <div
                className="cursor-pointer"
                onClick={
                  usedFor === "popup"
                    ? () => setShowKycPopup(false)
                    : () => navigate("/home")
                }
              >
                <img
                  src={Cancelimg}
                  alt="icon"
                  className="canceicon topRightIcon"
                />
              </div>
            )}
            <div className="row relativeposition">
              <div className="col-12 d-flex gap-4 align-items-center justify-content-center heading-section">
                <div className="authenticateid-header text-center">
                  <h4>
                    {isGuarantor
                      ? _t(
                          "authenticateTheGuarantorsId",
                          "Text used in authenticate scrren at onboarding process"
                        )
                      : _t(
                          "authenticateYourId",
                          "Text used in authenticate scrren at onboarding process"
                        )}{" "}
                    <span>
                      (
                      {_t(
                        "dniNieOrPassport",
                        "Text used in authenticate scrren at onboarding process"
                      )}
                      )
                    </span>
                  </h4>
                  <p>
                    {isGuarantor
                      ? _t(
                          "forYourGuarantorsVerificationAndToCompleteTheirProfilePleaseUploadTheirIdentityDocument",
                          "Text used in authenticate scrren at onboarding process"
                        )
                      : _t(
                          "forYourAccountSecurityAndStrongProfilePleaseCompleteIdentityVerfification",
                          "Text used in authenticate scrren at onboarding process"
                        )}
                  </p>
                </div>
              </div>
              {usedFor !== "popup" && (
                <div className="getback-btn">
                  <img
                    src={Backimg}
                    alt="icon"
                    className="canceicon"
                    onClick={
                      addedFiles?.length > 0
                        ? () => setAddedFiles([])
                        : formHandler.prev
                    }
                  />
                </div>
              )}
            </div>
            <div className="dg_paddingdiv">
              <div className="row uploaderWrapper justify-content-center">
                <div
                  className="col-md-10 col-12 dgboxsetup-wrapper"
                  style={{ opacity: addedFiles?.length > 0 ? "0.5" : "" }}
                >
                  {onfidoInitiateLoading ? (
                    <div className="dgboxsetup authenticateModal h-100 dgboxsetup2">
                      <div className="defaultSkorLoaderParent">
                        <img
                          src={SkorDefaultLoader}
                          className="defaultSkorLoader"
                          alt="Example"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="dgboxsetup dgboxsetup2 h-100">
                      <img
                        className="userimagesetup"
                        src={UserImg2}
                        alt="img"
                      />
                      <h6>{_t("digitalKYC")}</h6>
                      <h5 className="">
                        {isGuarantor
                          ? _t(
                              "haveYourGuarantorsOriginalPhysicalIdReadyOrAPictureOfItFrontAndBack",
                              "Text used in authenticate id scrren at onboarding process"
                            )
                          : _t(
                              "getYourOriginalPhysicalIdWithYou",
                              "Text used in authenticate id scrren at onboarding process"
                            )}
                      </h5>
                      <p className=" selectCountryText">
                        {_t(
                          "selectYourRespectiveCountryTakeAPictureAndUploadYourDocument",
                          "Text used in authenticate id scrren at onboarding process"
                        )}
                      </p>
                      <button
                        type="button"
                        className="btnNext btnnextpre continueButton position-relative w-100 uploadbtn continueButtonMobile"
                        disabled={
                          onfidoInitiateLoading || addedFiles?.length > 0
                        }
                        onClick={startCustomKyc}
                      >
                        {_t("continue")}
                        <img className="doc_Uploadimg" src={Arrow} alt="img" />
                      </button>
                      {showOnfidoWatingLoading && (
                        <p style={{ color: "gray" }}>
                          {_t("pleaseWaitWhileWeAreFetchingYourKycDetails")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center lockIconWrapper justify-content-center gap-1">
              <img className="lockIcon" src={lockIcon} alt="img" />
              <p className="lock-heading">
                {_t(
                  "yourDataIsEncrypted",
                  "Text used in authenticate id scrren at onboarding process"
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      <KycVerificationPopup
        showPopup={kycVerificationPopup}
        setKycVerificationPopup={setKycVerificationPopup}
        loadUser={loadUser}
        isGuarantor={isGuarantor}
      />
      <KycDocLoader isGuarantor={isGuarantor} showPopup={showKycLoading} />
      <ProcessFailedModal
        showPopup={failedKyc}
        isGuarantor={isGuarantor}
        heading={_t("kycVerificationFailed")}
        subheading={_t("yourKycVerificationHasBeenFailed")}
        closeModalState={setFailedKyc}
        hideBackgroundImage={undefined}
        buttonText={undefined}
      />
    </>
  );
};

export default AuthenticateYourId;
